/**
 * Per-diagram export helpers — SVG en PNG.
 *
 * **Letterlijke** export: we serialiseren rechtstreeks de DOM van de
 * React-Flow viewport via `html-to-image`, dus elke visuele override
 * (lijnstijl, custom kleuren, fonts, waypoints, drawing-rechthoeken,
 * label-achtergronden, attachment-dots — alles wat de gebruiker op
 * canvas ziet) komt 1-op-1 in de export terecht.
 *
 * Bounds-berekening loopt over alle `.react-flow__node`-elementen om de
 * model-ruimte (positie + size) op te tellen. We resetten de viewport-
 * transform naar identity en clampen size + transform op de bounding
 * box met wat padding, zodat het export-resultaat alle inhoud bevat
 * ongeacht de huidige zoom/scroll van de gebruiker.
 *
 * Trade-offs vs. een handmatig opnieuw-tekenen vanuit het domein-model:
 * - ✅ Letterlijk: alle visuele customisaties komen mee
 * - ✅ Eenvoudig te onderhouden — één render-pad i.p.v. twee
 * - ❌ ~30 KB extra bundle (html-to-image)
 * - ❌ Browser-quirks rond foreignObject in Safari (testen)
 */
import type { Concept, Diagram, Relation } from "@ae/domain";
import { toPng, toSvg } from "html-to-image";

interface ExportArgs {
  diagram: Diagram;
  concepts: Concept[];
  relations: Relation[];
  lang: "nl" | "en";
  /** Used as the filename stem (no extension). Falls back to "diagram". */
  filenameStem?: string;
}

/** Slugify a diagram name into a safe filename. */
function safeFilename(name: string): string {
  return (
    name
      .normalize("NFD")
      .replace(/\p{M}/gu, "") // strip diacritics (Unicode combining marks)
      .replace(/[^a-z0-9-_]+/gi, "_") // anything not safe → underscore
      .replace(/^_+|_+$/g, "") // trim
      .slice(0, 80) || "diagram"
  );
}

/** Trigger a browser download of an in-memory Blob or string. */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const PADDING = 24;

/**
 * Walk every `.react-flow__node` in the active canvas and read its
 * model-coordinate position + size. Returns null when the canvas is
 * not mounted or empty.
 *
 * Reads the `transform: translate(...)` attribute set by React Flow
 * (model-space pixel coords, not viewport-transformed) and the offset-
 * size of the wrapper. This bypasses the parent `.react-flow__viewport`
 * transform so the bounds we compute are in the same coordinate-space
 * as the viewport-content.
 */
function computeContentBounds(): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} | null {
  const nodeEls = document.querySelectorAll<HTMLElement>(".react-flow__node");
  if (nodeEls.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const el of nodeEls) {
    // React Flow zet `transform: translate(<x>px, <y>px)` op iedere
    // node — parse die naar model-coordinaten.
    const t = el.style.transform;
    const m = /translate\(([-\d.]+)px,\s*([-\d.]+)px\)/.exec(t);
    if (!m) continue;
    const x = Number.parseFloat(m[1]!);
    const y = Number.parseFloat(m[2]!);
    const w = el.offsetWidth || 140;
    const h = el.offsetHeight || 60;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x + w > maxX) maxX = x + w;
    if (y + h > maxY) maxY = y + h;
  }
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

/**
 * Find the React Flow viewport node for the currently mounted canvas.
 * Returns null when no canvas is in the DOM (e.g. user is on /settings).
 */
function findViewportEl(): HTMLElement | null {
  return document.querySelector<HTMLElement>(".react-flow__viewport");
}

/**
 * Common preprocess + html-to-image filter:
 *  - skip the React-Flow attribution badge (we hide it via prop, but
 *    just in case it's still in the DOM)
 *  - skip the Controls / MiniMap / Background — we want pure content
 *  - skip selection-rings / waypoint-handles? we keep those because
 *    they're part of the live canvas. If the user wants a "clean"
 *    export they can deselect first.
 *
 * Returns false to drop a node from the snapshot.
 */
const filterNode = (n: HTMLElement): boolean => {
  if (!(n instanceof Element)) return true;
  const cls = n.classList;
  if (!cls) return true;
  if (cls.contains("react-flow__attribution")) return false;
  if (cls.contains("react-flow__controls")) return false;
  if (cls.contains("react-flow__minimap")) return false;
  if (cls.contains("react-flow__background")) return false;
  return true;
};

/**
 * Build the html-to-image options that fit the viewport content into a
 * static image at scale 1. The viewport's existing transform is
 * overridden via the `style` field — html-to-image applies that style
 * to the *cloned* node it serializes, leaving the live canvas unchanged.
 */
function fitOptions(_viewport: HTMLElement) {
  void _viewport; // viewport is reserved for future filter-by-canvas logic
  const bounds = computeContentBounds();
  if (!bounds) return null;
  const width = Math.ceil(bounds.maxX - bounds.minX) + PADDING * 2;
  const height = Math.ceil(bounds.maxY - bounds.minY) + PADDING * 2;
  // The viewport itself has a transform: translate(...) scale(...);
  // we override it so the cloned snapshot starts at (0, 0) with scale 1
  // and shifts the content into the padding.
  const tx = -bounds.minX + PADDING;
  const ty = -bounds.minY + PADDING;
  return {
    width,
    height,
    backgroundColor: "#ffffff",
    pixelRatio: 1,
    cacheBust: true,
    filter: filterNode,
    style: {
      width: `${width}px`,
      height: `${height}px`,
      transform: `translate(${tx}px, ${ty}px) scale(1)`,
      transformOrigin: "0 0",
    } as Partial<CSSStyleDeclaration>,
  };
}

/**
 * Render het actieve canvas tot een letterlijke SVG-string en geef die
 * terug — geen download. Gebruikt door zowel `downloadDiagramSvg` als
 * door "Vraag Claude" zodat de prompt dezelfde visuele snapshot meekrijgt
 * die de gebruiker exporteert.
 *
 * Throws wanneer er geen canvas in de DOM staat of het diagram leeg is.
 */
export async function captureCanvasSvg(options: CaptureOptions = {}): Promise<string> {
  return decodeSvgDataUrl(await captureCanvasSvgDataUrl(options));
}

/**
 * Achtergrond van een vastlegging. Standaard wit (export). Vraag Claude
 * geeft de canvaskleur mee, zodat Claude krijgt wat de modelleur ziet.
 */
export interface CaptureOptions {
  background?: string;
}

/** De achtergrondkleur die het canvas nu toont (volgt licht/donker). */
export function canvasBackgroundColor(): string {
  const el = document.querySelector(".react-flow");
  const kleur = el ? getComputedStyle(el).getPropertyValue("--xy-background-color").trim() : "";
  return kleur || "#ffffff";
}

/**
 * Pak dezelfde canvas-snapshot maar retourneer de raw `data:image/svg+xml;…`
 * data-URL — ongedecodeerd. Gebruikt door de Vraag-Claude-preview om de
 * SVG via een `<img src="…">`-element te renderen i.p.v. via
 * `dangerouslySetInnerHTML`. Reden: Safari schaalt SVG-roots met intrinsic
 * `width`/`height`-attributen niet altijd via CSS `max-width: 100%`,
 * terwijl `<img>`-elementen consistent over alle browsers schalen.
 */
export async function captureCanvasSvgDataUrl(options: CaptureOptions = {}): Promise<string> {
  const viewport = findViewportEl();
  if (!viewport) {
    throw new Error("Geen actief canvas gevonden — open een diagram en probeer opnieuw.");
  }
  const opts = fitOptions(viewport);
  if (!opts) throw new Error("Diagram is leeg — er is niets om te exporteren.");
  return toSvg(viewport, {
    ...opts,
    ...(options.background ? { backgroundColor: options.background } : {}),
  });
}

/**
 * Render het canvas tot een PNG en geef base64-data terug (zonder
 * `data:image/png;base64,`-prefix — dat hoort de caller te kennen).
 *
 * Bedoeld voor multimodale Claude-input: Anthropic's vision API
 * tokeniseert een image op vrijwel constante kosten (~1500 tokens
 * ongeacht resolutie), waar SVG-tekst lineair tokeniseert op de
 * grootte. Voor grote canvases is een PNG dus radicaal compacter
 * qua context-budget.
 *
 * `pixelRatio` default 2 voor scherp display op retina; lager kan om
 * payload-grootte te beperken (1.5 ≈ 50% kleiner).
 */
export async function captureCanvasPngBase64(
  pixelRatio = 2,
  options: CaptureOptions = {},
): Promise<string> {
  const viewport = findViewportEl();
  if (!viewport) {
    throw new Error("Geen actief canvas gevonden — open een diagram en probeer opnieuw.");
  }
  const opts = fitOptions(viewport);
  if (!opts) throw new Error("Diagram is leeg — er is niets om te exporteren.");
  const dataUrl = await toPng(viewport, {
    ...opts,
    pixelRatio,
    ...(options.background ? { backgroundColor: options.background } : {}),
  });
  // toPng returns `data:image/png;base64,<...>`. Strip the prefix —
  // de caller stuurt dit als `source.data` in het Anthropic-payload-
  // shape `{type:"base64", media_type:"image/png", data:"..."}`.
  const idx = dataUrl.indexOf(",");
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
}

/** Export the active diagram as a literal SVG snapshot of the canvas. */
export async function downloadDiagramSvg(args: ExportArgs): Promise<void> {
  // Snapshot the viewport itself — all nodes and edge-SVG-layers live
  // inside it and share its coordinate space, so overriding the
  // viewport's transform via `opts.style` aligns the cloned content
  // back to (0,0) scale-1 with the right padding.
  const svg = await captureCanvasSvg();
  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const stem = safeFilename(args.filenameStem ?? "diagram");
  triggerDownload(blob, `${stem}.svg`);
}

/** Export the active diagram as a literal PNG snapshot of the canvas. */
export async function downloadDiagramPng(args: ExportArgs, scale = 2): Promise<void> {
  const viewport = findViewportEl();
  if (!viewport) {
    throw new Error("Geen actief canvas gevonden — open een diagram en probeer opnieuw.");
  }
  const opts = fitOptions(viewport);
  if (!opts) throw new Error("Diagram is leeg — er is niets om te exporteren.");
  const dataUrl = await toPng(viewport, { ...opts, pixelRatio: scale });
  const blob = await fetch(dataUrl).then((r) => r.blob());
  const stem = safeFilename(args.filenameStem ?? "diagram");
  triggerDownload(blob, `${stem}.png`);
}

/**
 * Decode a `data:image/svg+xml;charset=utf-8,<percent-encoded>` URL back
 * to the raw SVG string. html-to-image returns this URL form; we want
 * a clean .svg file on disk.
 */
function decodeSvgDataUrl(url: string): string {
  const prefixSemicolon = "data:image/svg+xml;charset=utf-8,";
  const prefixComma = "data:image/svg+xml,";
  let body: string;
  if (url.startsWith(prefixSemicolon)) {
    body = url.slice(prefixSemicolon.length);
  } else if (url.startsWith(prefixComma)) {
    body = url.slice(prefixComma.length);
  } else if (url.startsWith("data:image/svg+xml;base64,")) {
    return atob(url.slice("data:image/svg+xml;base64,".length));
  } else {
    return url; // unexpected; let caller handle
  }
  return decodeURIComponent(body);
}

// Concepts/Relations args are kept for backward compat with the export-
// menu but are no longer used inside — we read DOM directly. Suppress
// unused-warning by referencing them.
function _ignoreUnused(_args: ExportArgs): void {
  void _args.diagram;
  void _args.concepts;
  void _args.relations;
  void _args.lang;
}
void _ignoreUnused;
